import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import useEntriesService from "../services/useEntriesService";
import { Criteria, EntriesData, Options, SORT_OPTIONS, SORT_ORDER } from "../types/Entries";
import { useSocketContext, kSrvMsgIdentifier, EventTypes, EventSubtypes } from "./SocketContext";
import useToast, { ToastPosition, ToastType } from "../hooks/useToast";
import { useTranslation } from "react-i18next";
import { eventBus } from "../common/eventBus";

export const DEFAULT_OPTIONS: Options = {
    pageSize: 100,
    pageIndex: 0,
    sort: SORT_OPTIONS.TIMESTAMP,
    sortOrder: SORT_ORDER.DESC,
};

export const getDefaultFilters = (): Criteria => ({
    minDate: new Date().getTime(),
    maxDate: new Date().getTime(),
    licensePlate: '',
});

const EntriesContext = createContext<{
    entriesData: EntriesData;
    filters: Criteria;
    setFilters: (filters: Criteria) => void;
    options: Options;
    setOptions: (options: Options) => void;
    loading: boolean;
    showImages: boolean;
    setShowImages: (showImages: boolean) => void;
}>({
    entriesData: { entries: [], totalCount: 0 },
    filters: getDefaultFilters(),
    setFilters: () => { },
    options: DEFAULT_OPTIONS,
    setOptions: () => { },
    loading: false,
    showImages: true,
    setShowImages: () => { },
});

export const EntriesProvider = ({ children }: { children: ReactNode }) => {
    const [loading, setIsLoading] = useState(false);
    const [entriesData, setEntriesData] = useState<EntriesData>({ entries: [], totalCount: 0 });
    const [options, setOptions] = useState<Options>(DEFAULT_OPTIONS);
    const [filters, setFilters] = useState<Criteria>(getDefaultFilters);
    const [showImages, setShowImages] = useState(true);

    const { getEntries } = useEntriesService();
    const { socket } = useSocketContext();
    const { showToast } = useToast();
    const { t } = useTranslation("common")

    useEffect(() => {
        setIsLoading(true);
        getEntries(filters, options)
            .then((data) => {
                setEntriesData(data)
            })
            .catch(() => {
                showToast({
                    type: ToastType.ERROR,
                    message: t('common.unknownError'),
                    position: ToastPosition.TOP_CENTER,
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [filters, options, getEntries, showToast, t]);

    // handle live entry events
    useEffect(() => {
        const handleLiveEntry = (data: any) => {
            if (data.type === EventTypes.LIVE_EVENT && data.subtype === EventSubtypes.ANPR_ENTRY) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const filterMaxDate = new Date(filters.maxDate);
                filterMaxDate.setHours(0, 0, 0, 0);

                if (filterMaxDate.getTime() === today.getTime()) {
                    getEntries(filters, options)
                        .then((data) => {
                            setEntriesData(data)
                        })
                }
            }
        };

        socket?.on(kSrvMsgIdentifier, handleLiveEntry);

        return () => {
            socket?.off(kSrvMsgIdentifier, handleLiveEntry);
        };
    }, [socket, filters, options, getEntries]);

    // Handle eventBus events
    useEffect(() => {
        const carMarkedAsIrrelevantHandler = () => {
            getEntries(filters, options)
                .then((data) => {
                    setEntriesData(data)
                });
        };

        const entryDeletedHandler = () => {
            // todo maybe don't reload all entries but just remove the deleted one from state
            getEntries(filters, options)
                .then((data) => {
                    setEntriesData(data)
                });
        }

        eventBus.on('carMarkedAsIrrelevant', carMarkedAsIrrelevantHandler);
        eventBus.on('entryDeleted', entryDeletedHandler);

        return () => {
            eventBus.off('carMarkedAsIrrelevant', carMarkedAsIrrelevantHandler);
            eventBus.off('entryDeleted', entryDeletedHandler);
        }
    }, [filters, options, getEntries]);

    return (
        <EntriesContext.Provider
            value={{
                entriesData,
                filters,
                setFilters,
                options,
                setOptions,
                loading,
                showImages,
                setShowImages,
            }}>
            {children}
        </EntriesContext.Provider>
    )
};

export const useEntries = () => {
    const context = useContext(EntriesContext);
    if (!context) {
        throw new Error("useEntries must be used within an EntriesProvider");
    }
    return context;
};
